import React, { useState, useEffect } from 'react';
import { Cloud, Sun, CloudRain, CloudSnow, CloudLightning, Wind, Droplets, Thermometer } from 'lucide-react';

const WeatherWidget = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchWeather();
  }, []);

  const fetchWeather = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get user's location
      if (!navigator.geolocation) {
        setError('Geolocation not supported');
        return;
      }

      navigator.geolocation.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position.coords;

          // Use Open-Meteo API (free, no API key required)
          const response = await fetch(
            `https://api.open-meteo.com/v1/forecast?latitude=${latitude}&longitude=${longitude}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto`
          );

          if (response.ok) {
            const data = await response.json();
            setWeather({
              temp: Math.round(data.current.temperature_2m),
              humidity: data.current.relative_humidity_2m,
              windSpeed: Math.round(data.current.wind_speed_10m),
              weatherCode: data.current.weather_code,
              timezone: data.timezone
            });
          } else {
            setError('Failed to fetch weather');
          }
          setLoading(false);
        },
        (err) => {
          // Fallback to Fernandina Beach, FL if location denied
          fetchWeatherByCoords(30.6696, -81.4626);
        },
        { timeout: 5000 }
      );
    } catch (error) {
      console.error('Weather fetch error:', error);
      setError('Failed to load weather');
      setLoading(false);
    }
  };

  const fetchWeatherByCoords = async (lat, lon) => {
    try {
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m&temperature_unit=fahrenheit&wind_speed_unit=mph&timezone=auto`
      );

      if (response.ok) {
        const data = await response.json();
        setWeather({
          temp: Math.round(data.current.temperature_2m),
          humidity: data.current.relative_humidity_2m,
          windSpeed: Math.round(data.current.wind_speed_10m),
          weatherCode: data.current.weather_code,
          timezone: data.timezone,
          isDefault: true
        });
      } else {
        setError('Failed to fetch weather');
      }
    } catch (error) {
      setError('Failed to load weather');
    } finally {
      setLoading(false);
    }
  };

  // Map WMO weather codes to icons and descriptions
  const getWeatherInfo = (code) => {
    if (code === 0) return { icon: Sun, label: 'Clear', color: 'text-yellow-400' };
    if (code <= 3) return { icon: Cloud, label: 'Partly Cloudy', color: 'text-slate-300' };
    if (code <= 49) return { icon: Cloud, label: 'Foggy', color: 'text-slate-400' };
    if (code <= 59) return { icon: CloudRain, label: 'Drizzle', color: 'text-blue-300' };
    if (code <= 69) return { icon: CloudRain, label: 'Rain', color: 'text-blue-400' };
    if (code <= 79) return { icon: CloudSnow, label: 'Snow', color: 'text-blue-200' };
    if (code <= 84) return { icon: CloudRain, label: 'Showers', color: 'text-blue-400' };
    if (code <= 94) return { icon: CloudSnow, label: 'Snow Showers', color: 'text-blue-200' };
    if (code <= 99) return { icon: CloudLightning, label: 'Thunderstorm', color: 'text-yellow-300' };
    return { icon: Cloud, label: 'Unknown', color: 'text-slate-400' };
  };

  if (loading) {
    return (
      <div className="flex items-center gap-3 px-4 py-3 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
        <div className="w-5 h-5 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
        <span className="text-sm text-slate-400">Loading weather...</span>
      </div>
    );
  }

  if (error || !weather) {
    return null; // Silently hide if weather can't be loaded
  }

  const weatherInfo = getWeatherInfo(weather.weatherCode);
  const WeatherIcon = weatherInfo.icon;

  return (
    <div className="flex items-center gap-4 px-5 py-3 bg-slate-800/40 backdrop-blur-sm border border-slate-700/50 rounded-2xl">
      {/* Main temp and icon */}
      <div className="flex items-center gap-3">
        <WeatherIcon className={`w-10 h-10 ${weatherInfo.color}`} />
        <div>
          <div className="flex items-baseline gap-1">
            <span className="text-3xl font-bold text-white">{weather.temp}</span>
            <span className="text-lg text-slate-400">°F</span>
          </div>
          <p className="text-sm text-slate-400">{weatherInfo.label}</p>
        </div>
      </div>

      {/* Divider */}
      <div className="w-px h-12 bg-slate-600/50"></div>

      {/* Additional info */}
      <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Droplets className="w-4 h-4 text-blue-400" />
          <span>{weather.humidity}%</span>
        </div>
        <div className="flex items-center gap-2 text-sm text-slate-400">
          <Wind className="w-4 h-4 text-cyan-400" />
          <span>{weather.windSpeed} mph</span>
        </div>
      </div>
    </div>
  );
};

export default WeatherWidget;
